import { useEffect, useRef } from 'react';

const SignalGraph = ({ signals, width = '100%', height = 250 }) => {
    const canvasRef = useRef(null);
    const dataRef = useRef({
        rgb: [],
        pulse: []
    });

    // Use a Ref to store the latest signals to avoid re-triggering effects
    const latestSignalsRef = useRef(signals);
    useEffect(() => {
        latestSignalsRef.current = signals;
    }, [signals]);

    const maxPoints = 300; // 10 seconds at 30fps

    // Update data buffer - Decoupled from render loop
    useEffect(() => {
        if (!latestSignalsRef.current) return;

        const { rgb, pulse } = latestSignalsRef.current;
        const now = Date.now();

        // RGB
        if (rgb && rgb.length === 3) {
            dataRef.current.rgb.push({ t: now, val: rgb });
            if (dataRef.current.rgb.length > maxPoints) dataRef.current.rgb.shift();
        }

        // Pulse
        if (typeof pulse === 'number') {
            dataRef.current.pulse.push({ t: now, val: pulse });
            if (dataRef.current.pulse.length > maxPoints) dataRef.current.pulse.shift();
        }
    }, [signals]); // Sync on props change, but efficiently

    // Animation Loop
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d', { alpha: false }); // Optimize for no transparency

        let animationFrameId;

        const render = () => {
            // Handle resizing intelligently
            // Only set width/height if they actually changed to prevent flickering from layout thrashing
            const rect = canvas.getBoundingClientRect();
            if (canvas.width !== rect.width || canvas.height !== rect.height) {
                canvas.width = rect.width;
                canvas.height = rect.height;
            }

            const w = canvas.width;
            const h = canvas.height;
            const hHalf = h / 2;

            // Use clearRect cleanly
            ctx.clearRect(0, 0, w, h);

            // Draw Background
            ctx.fillStyle = '#111';
            ctx.fillRect(0, 0, w, h);

            // Draw Divider
            ctx.strokeStyle = '#333';
            ctx.beginPath();
            ctx.moveTo(0, hHalf);
            ctx.lineTo(w, hHalf);
            ctx.stroke();

            // Helper to map time to x
            const now = Date.now();
            const timeWindow = 10000; // 10s window matching maxPoints
            const getX = (t) => w - ((now - t) / timeWindow) * w;

            // DRAW RGB (Top Half)
            const rgbData = dataRef.current.rgb;
            if (rgbData.length > 1) {
                const reds = rgbData.map(d => d.val[0]);
                const greens = rgbData.map(d => d.val[1]);
                const blues = rgbData.map(d => d.val[2]);

                // Global scaling for stability
                const min = Math.min(...reds, ...greens, ...blues) * 0.99;
                const max = Math.max(...reds, ...greens, ...blues) * 1.01;
                const range = max - min || 1;

                const normalize = (v) => hHalf - ((v - min) / range) * (hHalf - 20) - 10;

                const drawChannel = (vals, color) => {
                    ctx.strokeStyle = color;
                    ctx.lineWidth = 1.5;
                    ctx.beginPath();
                    // Optimization: Only draw points that are visible (within timeWindow)

                    let started = false;
                    for (let i = 0; i < vals.length; i++) {
                        const t = rgbData[i].t;
                        // Skip old points
                        if (now - t > timeWindow) continue;

                        const x = getX(t);
                        const y = normalize(vals[i]);

                        if (!started) {
                            ctx.moveTo(x, y);
                            started = true;
                        } else {
                            ctx.lineTo(x, y);
                        }
                    }
                    ctx.stroke();
                };

                drawChannel(reds, '#ff4444');
                drawChannel(greens, '#00ff88');
                drawChannel(blues, '#4488ff');

                // Labels
                ctx.fillStyle = '#fff';
                ctx.font = '10px monospace';
                ctx.fillText("Raw RGB", 5, 15);
            }

            // DRAW PULSE (Bottom Half)
            const pulseData = dataRef.current.pulse;
            if (pulseData.length > 1) {
                const vals = pulseData.map(d => d.val);
                const min = Math.min(...vals);
                const max = Math.max(...vals);
                const range = max - min || 1;

                const normalize = (v) => h - ((v - min) / range) * (hHalf - 30) - 15;

                ctx.strokeStyle = '#ffff00';
                ctx.lineWidth = 2;
                ctx.beginPath();

                let started = false;
                for (let i = 0; i < vals.length; i++) {
                    const t = pulseData[i].t;
                    if (now - t > timeWindow) continue;

                    const x = getX(t);
                    const y = normalize(vals[i]);

                    if (!started) {
                        ctx.moveTo(x, y);
                        started = true;
                    } else {
                        ctx.lineTo(x, y);
                    }
                }
                ctx.stroke();

                ctx.fillStyle = '#fff';
                ctx.fillText("Pulse Waveform (BVP)", 5, hHalf + 15);
            }

            animationFrameId = requestAnimationFrame(render);
        };

        render();
        return () => cancelAnimationFrame(animationFrameId);
    }, []);

    return (
        <div className={`relative w-full h-full bg-black rounded-lg overflow-hidden border border-border/20 shadow-inner ${width === '100%' ? '' : `w-[${width}] top-0 left-0 right-0 bottom-0 pointer-events-none`}`}>
            <canvas ref={canvasRef} className="w-full h-full block" />
        </div>
    );
};

export default SignalGraph;
